import React, { useState, useMemo } from 'react';
import {
  Plus,
  Trash2,
  X,
  Loader2,
  Pencil,
  Building2,
  Wrench,
  ShieldCheck,
  Trees,
  Cpu,
  Sparkles,
  HardHat,
  CircleDot,
  Eye,
  Archive,
  TrendingUp,
  MessageSquare,
  HelpCircle,
  Image as ImageIcon,
  FileText,
  Copy,
  Send,
} from 'lucide-react';
import { useSection, useServices, useDeleteConfirmation, useToast } from '@/app/providers';
import { SectionMultiSelect } from '@/modules/sections/ui/components/SectionMultiSelect';
import {
  TransparencyProject,
  TransparencyProjectCategory,
  TransparencyProjectPriority,
  TransparencyProjectStatus,
  type TransparencyProjectView,
} from '@/shared/types';
import { Button } from '../../../../presentation/components/atoms/Button';
import { Input } from '../../../../presentation/components/atoms/Input';
import { Select } from '../../../../presentation/components/atoms/Select';
import { Checkbox } from '../../../../presentation/components/atoms/Checkbox';
import { useCrudNotifications, usePaginatedList, useFileUpload, useDateFormatter } from '@/shared/hooks';
import { DataTable, RowActionsMenu, type ColumnDef } from '@/core/ui/components';
import { useAbility } from '@/modules/identity/application/ability';
import { ProjectDetailModal } from './ProjectDetailModal';
import { AddUpdateModal } from './AddUpdateModal';
import { ProjectKpiRow } from './ProjectKpiRow';

const VIEW_TABS: { value: TransparencyProjectView; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'completed', label: 'Finalizados' },
  { value: 'archived', label: 'Archivados' },
];

const CATEGORY_OPTIONS = Object.values(TransparencyProjectCategory).map((c) => ({
  value: c,
  label: {
    MAINTENANCE: 'Mantenimiento',
    SECURITY: 'Seguridad',
    REMODELING: 'Remodelación',
    LANDSCAPING: 'Jardinería',
    INFRASTRUCTURE: 'Infraestructura',
    AMENITIES: 'Amenidades',
    TECHNOLOGY: 'Tecnología',
    OTHER: 'Otro',
  }[c],
}));

const PRIORITY_OPTIONS = Object.values(TransparencyProjectPriority).map((p) => ({
  value: p,
  label: { LOW: 'Baja', MEDIUM: 'Media', HIGH: 'Alta', URGENT: 'Urgente' }[p],
}));

const STATUS_OPTIONS = Object.values(TransparencyProjectStatus).map((s) => ({
  value: s,
  label: {
    PLANNING: 'Planeación',
    APPROVED: 'Aprobado',
    IN_PROGRESS: 'En progreso',
    PAUSED: 'Pausado',
    DELAYED: 'Retrasado',
    COMPLETED: 'Completado',
    CANCELLED: 'Cancelado',
  }[s],
}));

const CATEGORY_ICON: Record<TransparencyProjectCategory, React.ReactNode> = {
  [TransparencyProjectCategory.MAINTENANCE]: <Wrench size={12} />,
  [TransparencyProjectCategory.SECURITY]: <ShieldCheck size={12} />,
  [TransparencyProjectCategory.REMODELING]: <HardHat size={12} />,
  [TransparencyProjectCategory.LANDSCAPING]: <Trees size={12} />,
  [TransparencyProjectCategory.INFRASTRUCTURE]: <Building2 size={12} />,
  [TransparencyProjectCategory.AMENITIES]: <Sparkles size={12} />,
  [TransparencyProjectCategory.TECHNOLOGY]: <Cpu size={12} />,
  [TransparencyProjectCategory.OTHER]: <CircleDot size={12} />,
};

const PRIORITY_COLOR: Record<TransparencyProjectPriority, string> = {
  [TransparencyProjectPriority.LOW]: 'bg-slate-100 text-slate-600 border-slate-200',
  [TransparencyProjectPriority.MEDIUM]: 'bg-blue-50 text-blue-700 border-blue-200',
  [TransparencyProjectPriority.HIGH]: 'bg-amber-100 text-amber-700 border-amber-200',
  [TransparencyProjectPriority.URGENT]: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_COLOR: Record<TransparencyProjectStatus, string> = {
  [TransparencyProjectStatus.PLANNING]: 'bg-slate-100 text-slate-600',
  [TransparencyProjectStatus.APPROVED]: 'bg-cyan-100 text-cyan-700',
  [TransparencyProjectStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-700',
  [TransparencyProjectStatus.PAUSED]: 'bg-amber-100 text-amber-700',
  [TransparencyProjectStatus.DELAYED]: 'bg-orange-100 text-orange-700',
  [TransparencyProjectStatus.COMPLETED]: 'bg-green-100 text-green-700',
  [TransparencyProjectStatus.CANCELLED]: 'bg-red-100 text-red-700',
};

export const AdminProjectList = () => {
  const { formatRelativeTime } = useDateFormatter();
  const ability = useAbility();
  const toast = useToast();
  const { confirmDelete } = useDeleteConfirmation();
  const { transparencyProjectRepo } = useServices();
  const { hasSections } = useSection();
  const { withCreateNotification, withDeleteNotification, withUpdateNotification } = useCrudNotifications({
    entityName: 'Proyecto',
  });
  const canCreate = ability.can('manage', 'all') || ability.can('create', 'TransparencyProject');
  const canUpdate = ability.can('manage', 'all') || ability.can('update', 'TransparencyProject');
  const canDelete = ability.can('manage', 'all') || ability.can('delete', 'TransparencyProject');

  const [view, setView] = useState<TransparencyProjectView>('all');
  const [kpiRefreshKey, setKpiRefreshKey] = useState(0);

  const {
    data: projects,
    meta,
    loading,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    setFilters,
    refresh,
  } = usePaginatedList<TransparencyProject, { view: TransparencyProjectView }>({
    fetchFn: (params) => transparencyProjectRepo.getProjects(params),
    initialFilters: { view: 'all' },
    errorTitle: 'Error al cargar proyectos',
  });

  /** Refresca tabla y KPIs juntos: cualquier acción cambia ambos. */
  const refreshAll = () => {
    refresh();
    setKpiRefreshKey((k) => k + 1);
  };

  const changeView = (next: TransparencyProjectView) => {
    setView(next);
    setFilters({ view: next });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isBusyId, setIsBusyId] = useState<string | null>(null);
  const [detailProject, setDetailProject] = useState<TransparencyProject | null>(null);
  const [updateProject, setUpdateProject] = useState<TransparencyProject | null>(null);

  const { upload, isUploading, uploadedFile, reset: resetUpload } = useFileUpload('transparency_images');

  // Form state
  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TransparencyProjectCategory>(TransparencyProjectCategory.OTHER);
  const [priority, setPriority] = useState<TransparencyProjectPriority>(TransparencyProjectPriority.MEDIUM);
  const [status, setStatus] = useState<TransparencyProjectStatus>(TransparencyProjectStatus.PLANNING);
  const [startDate, setStartDate] = useState('');
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState('');
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [communityLocation, setCommunityLocation] = useState('');
  const [budget, setBudget] = useState('');
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [questionsEnabled, setQuestionsEnabled] = useState(true);
  const [sectionIds, setSectionIds] = useState<string[]>([]);

  const resetForm = () => {
    setTitle('');
    setShortDescription('');
    setDescription('');
    setCategory(TransparencyProjectCategory.OTHER);
    setPriority(TransparencyProjectPriority.MEDIUM);
    setStatus(TransparencyProjectStatus.PLANNING);
    setStartDate('');
    setEstimatedCompletionDate('');
    setResponsiblePerson('');
    setCommunityLocation('');
    setBudget('');
    setCommentsEnabled(true);
    setQuestionsEnabled(true);
    setSectionIds([]);
    resetUpload();
    setEditingId(null);
    setIsEditMode(false);
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (p: TransparencyProject) => {
    if (!canUpdate) return;
    setTitle(p.title);
    setShortDescription(p.shortDescription || '');
    setDescription(p.description);
    setCategory(p.category);
    setPriority(p.priority);
    setStatus(p.status);
    setStartDate(p.startDate || '');
    setEstimatedCompletionDate(p.estimatedCompletionDate || '');
    setResponsiblePerson(p.responsiblePerson || '');
    setCommunityLocation(p.communityLocation || '');
    setBudget(p.budget || '');
    setCommentsEnabled(p.commentsEnabled);
    setQuestionsEnabled(p.questionsEnabled);
    setSectionIds(p.sectionIds ?? []);
    setEditingId(p.id);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, projectTitle: string) => {
    if (!canDelete) return;
    await confirmDelete({
      itemName: projectTitle,
      itemType: 'Proyecto',
      onConfirm: async () => {
        await withDeleteNotification(() => transparencyProjectRepo.deleteProject(id));
        refreshAll();
      },
    });
  };

  const handlePublish = async (p: TransparencyProject) => {
    if (!canUpdate) return;
    setIsBusyId(p.id);
    try {
      await transparencyProjectRepo.publishProject(p.id);
      toast.success('Proyecto publicado');
      refreshAll();
    } catch {
      toast.error('Error al publicar el proyecto');
    } finally {
      setIsBusyId(null);
    }
  };

  const handleNotify = async (p: TransparencyProject) => {
    if (!canUpdate) return;
    setIsBusyId(p.id);
    try {
      await transparencyProjectRepo.notifyResidents(p.id);
      toast.success('Notificación enviada a los residentes');
    } catch {
      toast.error('Solo se pueden notificar proyectos publicados');
    } finally {
      setIsBusyId(null);
    }
  };

  /** Copia los campos estables; la bitácora y la participación no se duplican. */
  const handleDuplicate = async (p: TransparencyProject) => {
    if (!canCreate) return;
    setIsBusyId(p.id);
    try {
      await withCreateNotification(() =>
        transparencyProjectRepo.createProject({
          title: `${p.title} (copia)`,
          shortDescription: p.shortDescription ?? undefined,
          description: p.description,
          category: p.category,
          priority: p.priority,
          responsiblePerson: p.responsiblePerson ?? undefined,
          communityLocation: p.communityLocation ?? undefined,
          budget: p.budget ?? undefined,
          commentsEnabled: p.commentsEnabled,
          questionsEnabled: p.questionsEnabled,
        })
      );
      refreshAll();
    } catch {
      // El hook ya notificó el error.
    } finally {
      setIsBusyId(null);
    }
  };

  const handleArchive = async (p: TransparencyProject) => {
    if (!canUpdate) return;
    setIsBusyId(p.id);
    try {
      await transparencyProjectRepo.archiveProject(p.id);
      toast.success('Proyecto archivado');
      refreshAll();
    } catch {
      toast.error('Error al archivar el proyecto');
    } finally {
      setIsBusyId(null);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !description) return;
    if (isEditMode && !canUpdate) return;
    if (!isEditMode && !canCreate) return;

    try {
      const payload = {
        title,
        shortDescription: shortDescription || undefined,
        description,
        category,
        priority,
        status,
        startDate: startDate || undefined,
        estimatedCompletionDate: estimatedCompletionDate || undefined,
        responsiblePerson: responsiblePerson || undefined,
        communityLocation: communityLocation || undefined,
        budget: budget || undefined,
        coverImageFileId: uploadedFile?.id,
        commentsEnabled,
        questionsEnabled,
        sectionIds,
      };

      if (isEditMode && editingId) {
        await withUpdateNotification(() => transparencyProjectRepo.updateProject(editingId, payload));
      } else {
        await withCreateNotification(() => transparencyProjectRepo.createProject(payload));
      }

      setIsModalOpen(false);
      resetForm();
      refreshAll();
    } catch {
      // Error ya mostrado por el hook
    }
  };

  const getCategoryBadge = (c: TransparencyProjectCategory) => (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
      {CATEGORY_ICON[c]} {CATEGORY_OPTIONS.find((o) => o.value === c)?.label}
    </span>
  );

  const getPriorityBadge = (p: TransparencyProjectPriority) => (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${PRIORITY_COLOR[p]}`}>
      {PRIORITY_OPTIONS.find((o) => o.value === p)?.label}
    </span>
  );

  const getStatusBadge = (s: TransparencyProjectStatus) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[s]}`}>
      {STATUS_OPTIONS.find((o) => o.value === s)?.label}
    </span>
  );

  const getVisibilityBadge = (v: TransparencyProject['visibility']) => {
    if (v === 'PUBLISHED') return <span className="text-xs text-green-600 font-medium">Publicado</span>;
    if (v === 'ARCHIVED') return <span className="text-xs text-slate-400 font-medium">Archivado</span>;
    return <span className="text-xs text-slate-500 font-medium">Borrador</span>;
  };

  const columns: ColumnDef<TransparencyProject>[] = useMemo(
    () => [
      {
        id: 'title',
        header: 'Proyecto',
        cell: (p) => (
          <div>
            <p className="font-bold text-slate-800">{p.title}</p>
            <p className="text-xs text-slate-500 line-clamp-2 max-w-xs">{p.shortDescription}</p>
          </div>
        ),
      },
      { id: 'category', header: 'Categoría', cell: (p) => getCategoryBadge(p.category) },
      { id: 'priority', header: 'Prioridad', cell: (p) => getPriorityBadge(p.priority) },
      {
        // Estado y avance juntos: son la misma pregunta ("¿cómo va?") y separarlos
        // costaba una columna extra en una tabla que ya venía apretada.
        id: 'status',
        header: 'Estado',
        cell: (p) => (
          <div className="w-28 space-y-1.5">
            {getStatusBadge(p.status)}
            <div className="flex items-center gap-1.5">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-primary-500" style={{ width: `${p.progress}%` }} />
              </div>
              <span className="text-[11px] text-slate-400 tabular-nums">{p.progress}%</span>
            </div>
          </div>
        ),
      },
      { id: 'visibility', header: 'Visibilidad', cell: (p) => getVisibilityBadge(p.visibility) },
      {
        id: 'engagement',
        header: 'Participación',
        cell: (p) => {
          const c = p.counts;
          if (!c || (!c.comments && !c.questions && !c.images && !c.documents)) {
            return <span className="text-slate-300 text-sm">—</span>;
          }
          return (
            <div className="flex items-center gap-2.5 text-xs text-slate-500">
              {c.comments > 0 && (
                <span className="flex items-center gap-1" title={`${c.comments} comentarios`}>
                  <MessageSquare size={12} /> {c.comments}
                </span>
              )}
              {c.questions > 0 && (
                <span className="flex items-center gap-1" title={`${c.questions} preguntas`}>
                  <HelpCircle size={12} /> {c.questions}
                </span>
              )}
              {c.images > 0 && (
                <span className="flex items-center gap-1" title={`${c.images} fotos`}>
                  <ImageIcon size={12} /> {c.images}
                </span>
              )}
              {c.documents > 0 && (
                <span className="flex items-center gap-1" title={`${c.documents} documentos`}>
                  <FileText size={12} /> {c.documents}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: 'lastActivity',
        header: 'Última actividad',
        cell: (p) =>
          p.lastActivityAt ? (
            <div className="max-w-[200px]">
              <p className="text-sm text-slate-600">{formatRelativeTime(p.lastActivityAt)}</p>
              <p className="text-xs text-slate-400 line-clamp-1">{p.lastActivityTitle}</p>
            </div>
          ) : (
            <span className="text-slate-400 text-sm">Sin actualizaciones</span>
          ),
      },
    ],
    [formatRelativeTime]
  );

  return (
    <>
      <ProjectKpiRow refreshKey={kpiRefreshKey} />

      <DataTable
        data={projects}
        columns={columns}
        keyExtractor={(p) => p.id}
        meta={meta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        title="Transparencia — Proyectos"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar proyecto..."
        loading={loading}
        emptyMessage={
          view === 'archived'
            ? 'No hay proyectos archivados.'
            : view === 'completed'
              ? 'Aún no hay proyectos finalizados.'
              : 'No hay proyectos registrados.'
        }
        filters={
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => changeView(tab.value)}
                className={`px-3 h-[38px] rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  view === tab.value
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
        actions={
          canCreate ? (
            <Button onClick={openCreateModal} icon={<Plus size={16} />}>
              Nuevo proyecto
            </Button>
          ) : null
        }
        rowActions={(p) => (
          <RowActionsMenu>
            {(close) => (
              <>
                {canUpdate && (
                  <button
                    onClick={() => {
                      setUpdateProject(p);
                      close();
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 transition-colors whitespace-nowrap"
                  >
                    <TrendingUp size={16} className="text-primary-600 shrink-0" /> Agregar actualización
                  </button>
                )}
                <button
                  onClick={() => {
                    setDetailProject(p);
                    close();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  <Eye size={16} className="text-blue-500 shrink-0" /> Ver detalle / Bitácora
                </button>
                {canUpdate && (
                  <>
                    <button
                      onClick={() => {
                        openEditModal(p);
                        close();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                      <Pencil size={16} className="text-amber-500 shrink-0" /> Editar
                    </button>
                    {p.visibility !== 'PUBLISHED' && (
                      <button
                        onClick={() => {
                          handlePublish(p);
                          close();
                        }}
                        disabled={isBusyId === p.id}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        {isBusyId === p.id ? (
                          <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />
                        ) : (
                          <Eye size={16} className="text-green-500 shrink-0" />
                        )}
                        Publicar
                      </button>
                    )}
                    {p.visibility === 'PUBLISHED' && (
                      <button
                        onClick={() => {
                          handleNotify(p);
                          close();
                        }}
                        disabled={isBusyId === p.id}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <Send size={16} className="text-cyan-500 shrink-0" /> Notificar a residentes
                      </button>
                    )}
                    {canCreate && (
                      <button
                        onClick={() => {
                          handleDuplicate(p);
                          close();
                        }}
                        disabled={isBusyId === p.id}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <Copy size={16} className="text-slate-500 shrink-0" /> Duplicar
                      </button>
                    )}
                    {p.visibility !== 'ARCHIVED' && (
                      <button
                        onClick={() => {
                          handleArchive(p);
                          close();
                        }}
                        disabled={isBusyId === p.id}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <Archive size={16} className="text-slate-500 shrink-0" /> Archivar
                      </button>
                    )}
                  </>
                )}
                {canDelete && (
                  <>
                    <hr className="my-1 border-slate-100" />
                    <button
                      onClick={() => {
                        handleDelete(p.id, p.title);
                        close();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
                    >
                      <Trash2 size={16} className="shrink-0" /> Eliminar
                    </button>
                  </>
                )}
              </>
            )}
          </RowActionsMenu>
        )}
      />

      {isModalOpen && ((isEditMode && canUpdate) || (!isEditMode && canCreate)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
              <h3 className="font-bold text-slate-800 text-lg">
                {isEditMode ? 'Editar proyecto' : 'Nuevo proyecto'}
              </h3>
              <button onClick={() => { setIsModalOpen(false); resetForm(); }}>
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <Input
                label="Título"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej. Remodelación de alberca"
                required
              />

              <Input
                label="Descripción corta"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Resumen breve visible en la lista"
              />

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Descripción</label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe el proyecto en detalle..."
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Select
                  label="Categoría"
                  value={category}
                  onChange={(v) => setCategory(v as TransparencyProjectCategory)}
                  options={CATEGORY_OPTIONS}
                />
                <Select
                  label="Prioridad"
                  value={priority}
                  onChange={(v) => setPriority(v as TransparencyProjectPriority)}
                  options={PRIORITY_OPTIONS}
                />
                <Select
                  label="Estado"
                  value={status}
                  onChange={(v) => setStatus(v as TransparencyProjectStatus)}
                  options={STATUS_OPTIONS}
                />
              </div>

              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                El avance del proyecto se mueve publicando actualizaciones en la bitácora, no desde
                acá — así ningún cambio queda sin explicación para el residente.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fecha de inicio"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label="Fecha estimada de fin"
                  type="date"
                  value={estimatedCompletionDate}
                  onChange={(e) => setEstimatedCompletionDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Responsable"
                  value={responsiblePerson}
                  onChange={(e) => setResponsiblePerson(e.target.value)}
                  placeholder="Nombre del responsable"
                />
                <Input
                  label="Ubicación"
                  value={communityLocation}
                  onChange={(e) => setCommunityLocation(e.target.value)}
                  placeholder="Ej. Área de alberca"
                />
              </div>

              {hasSections && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Visibilidad
                  </label>
                  <SectionMultiSelect
                    value={sectionIds}
                    onChange={setSectionIds}
                    resourceLabel="proyecto"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 items-end">
                <Input
                  label="Presupuesto (opcional)"
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="0.00"
                />
                <label
                  className={`cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 transition-colors w-full h-[42px] ${isUploading ? 'opacity-50 cursor-wait' : ''}`}
                >
                  {isUploading ? (
                    <Loader2 size={16} className="mr-2 text-slate-500 animate-spin" />
                  ) : (
                    <Plus size={16} className="mr-2 text-slate-500" />
                  )}
                  {isUploading ? 'Subiendo...' : uploadedFile ? 'Portada OK' : 'Imagen de portada'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await upload(file);
                    }}
                  />
                </label>
              </div>

              <div className="flex gap-6 pt-2">
                <Checkbox label="Permitir comentarios" checked={commentsEnabled} onChange={setCommentsEnabled} />
                <Checkbox label="Permitir preguntas" checked={questionsEnabled} onChange={setQuestionsEnabled} />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={() => { setIsModalOpen(false); resetForm(); }}
                >
                  Cerrar
                </Button>
                <Button type="submit" variant="primary" className="flex-1">
                  {isEditMode ? 'Guardar cambios' : 'Crear proyecto'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detailProject && (
        <ProjectDetailModal project={detailProject} onClose={() => setDetailProject(null)} />
      )}

      {updateProject && (
        <AddUpdateModal
          project={updateProject}
          onClose={() => setUpdateProject(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
};
